import React, { useMemo } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useWarehouses } from '@/hooks/useInventory';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { ItemSelect, type ItemLike } from '@/components/inventory/ItemSelect';
import warehouseLocationService from '@/services/warehouseLocation.service';
import api from '@/services/api.service';

const { Title, Text } = Typography;

const OWNERSHIP_OPTIONS = [
  { value: 'COMPANY', label: 'Company' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'SUPPLIER_CONSIGNMENT', label: 'Supplier consignment' },
];

function unwrapList<T>(res: unknown): T[] {
  const body = (res as { data?: unknown })?.data ?? res;
  const list = (body as { data?: unknown })?.data ?? body;
  return Array.isArray(list) ? (list as T[]) : [];
}

export const PutawayPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { data: warehouses = [] } = useWarehouses();
  const warehouseId = Form.useWatch('warehouse_id', form);
  const ownershipType = Form.useWatch('ownership_type', form) || 'COMPANY';
  const { data: customers = [] } = useActiveCustomers();

  const { data: locations = [], isLoading: locLoading } = useQuery({
    queryKey: ['warehouse-locations', warehouseId],
    queryFn: () => warehouseLocationService.listByWarehouse(warehouseId),
    enabled: !!warehouseId,
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['procurement-suppliers'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      return unwrapList<{ id: string; supplier_code?: string; supplier_name?: string; is_active?: boolean }>(
        res
      ).filter((s) => s.is_active !== false);
    },
  });

  const binOptions = useMemo(
    () =>
      locations
        .filter((l) => l.is_active !== false)
        .map((l) => ({
          value: l.id,
          label: `${l.location_code} — ${l.location_name}`,
        })),
    [locations]
  );

  const warehouseOptions = useMemo(
    () =>
      (warehouses as Array<{ id: string; warehouse_code?: string; warehouse_name?: string }>).map(
        (w) => ({
          value: w.id,
          label: w.warehouse_code
            ? `${w.warehouse_code} — ${w.warehouse_name}`
            : w.warehouse_name || w.id,
        })
      ),
    [warehouses]
  );

  const customerOptions = useMemo(
    () =>
      (customers as Array<{ id: string; customer_name?: string; customer_code?: string }>).map(
        (c) => ({
          value: c.id,
          label: c.customer_code
            ? `${c.customer_code} — ${c.customer_name}`
            : c.customer_name || c.id,
        })
      ),
    [customers]
  );

  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: s.id,
        label: s.supplier_code
          ? `${s.supplier_code} — ${s.supplier_name}`
          : s.supplier_name || s.id,
      })),
    [suppliers]
  );

  const putawayMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      warehouseLocationService.putaway({
        warehouse_id: String(values.warehouse_id),
        item_id: String(values.item_id),
        from_location_id: (values.from_location_id as string) || null,
        to_location_id: String(values.to_location_id),
        quantity: Number(values.quantity),
        uom: String(values.uom || 'PCS'),
        heat_number: (values.heat_number as string) || null,
        dimension: (values.dimension as string) || null,
        ownership_type: (values.ownership_type as string) || 'COMPANY',
        owner_customer_id:
          values.ownership_type === 'CUSTOMER'
            ? (values.owner_customer_id as string) || null
            : null,
        owner_supplier_id:
          values.ownership_type === 'SUPPLIER_CONSIGNMENT'
            ? (values.owner_supplier_id as string) || null
            : null,
        remarks: (values.remarks as string) || null,
      }),
    onSuccess: (res) => {
      message.success(`Putaway posted${res?.voucherNumber ? ` (${res.voucherNumber})` : ''}`);
      form.resetFields();
      form.setFieldsValue({ ownership_type: 'COMPANY', uom: 'PCS' });
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Putaway failed'),
  });

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Bin putaway
            </Title>
            <Text type="secondary">
              Move stock between bins in the same warehouse. Ownership, heat, and dimension are
              preserved.
            </Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            initialValues={{ ownership_type: 'COMPANY', uom: 'PCS' }}
            onFinish={(values) => putawayMut.mutate(values)}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="warehouse_id"
                  label="Warehouse"
                  rules={[{ required: true, message: 'Warehouse required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={warehouseOptions}
                    placeholder="Select warehouse"
                    onChange={() =>
                      form.setFieldsValue({
                        from_location_id: undefined,
                        to_location_id: undefined,
                      })
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="item_id"
                  label="Item"
                  rules={[{ required: true, message: 'Item required' }]}
                >
                  <ItemSelect
                    style={{ width: '100%' }}
                    placeholder="Select item"
                    onChange={(_id, option) => {
                      const item = (option as { item?: ItemLike } | undefined)?.item;
                      if (item?.base_uom) form.setFieldsValue({ uom: item.base_uom });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="from_location_id" label="From bin">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    loading={locLoading}
                    disabled={!warehouseId}
                    options={binOptions}
                    placeholder="Source bin (blank = warehouse root)"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="to_location_id"
                  label="To bin"
                  rules={[{ required: true, message: 'Destination bin required' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={locLoading}
                    disabled={!warehouseId}
                    options={binOptions}
                    placeholder="Destination bin"
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item
                  name="quantity"
                  label="Quantity"
                  rules={[{ required: true, message: 'Qty required' }]}
                >
                  <InputNumber min={0.0001} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name="uom" label="UoM" rules={[{ required: true }]}>
                  <Input placeholder="PCS" />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name="heat_number" label="Heat no.">
                  <Input allowClear placeholder="Optional" />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item name="dimension" label="Dimension">
                  <Input allowClear placeholder="Optional" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="ownership_type" label="Ownership" rules={[{ required: true }]}>
                  <Select
                    options={OWNERSHIP_OPTIONS}
                    onChange={(v) => {
                      if (v !== 'CUSTOMER') form.setFieldsValue({ owner_customer_id: undefined });
                      if (v !== 'SUPPLIER_CONSIGNMENT')
                        form.setFieldsValue({ owner_supplier_id: undefined });
                    }}
                  />
                </Form.Item>
              </Col>
              {ownershipType === 'CUSTOMER' && (
                <Col xs={24} md={8}>
                  <Form.Item
                    name="owner_customer_id"
                    label="Owner customer"
                    rules={[{ required: true, message: 'Customer required' }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      options={customerOptions}
                      placeholder="Customer"
                    />
                  </Form.Item>
                </Col>
              )}
              {ownershipType === 'SUPPLIER_CONSIGNMENT' && (
                <Col xs={24} md={8}>
                  <Form.Item
                    name="owner_supplier_id"
                    label="Owner supplier"
                    rules={[{ required: true, message: 'Supplier required' }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      options={supplierOptions}
                      placeholder="Supplier"
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24}>
                <Form.Item name="remarks" label="Remarks">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<CloudUploadOutlined />}
                loading={putawayMut.isPending}
              >
                Post putaway
              </Button>
              <Button onClick={() => navigate('/scm/inventory/stock-summary')}>
                Stock on hand
              </Button>
            </Space>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default PutawayPage;
